import api from './api';
import { ApiResponse } from '../types';

export interface Configuration {
  id: number;
  chave: string;
  valor: string;
  descricao: string;
  tipo: 'string' | 'number' | 'boolean' | 'json';
  categoria: string;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateConfigurationRequest {
  chave: string;
  valor: string;
  descricao: string;
  tipo: 'string' | 'number' | 'boolean' | 'json';
  categoria: string;
  ativo?: boolean;
}

export interface UpdateConfigurationRequest {
  valor?: string;
  descricao?: string;
  tipo?: 'string' | 'number' | 'boolean' | 'json';
  categoria?: string;
  ativo?: boolean;
}

export interface TestOpenAIResponse {
  success: boolean;
  message: string;
  model?: string;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export interface TestSmtpPayload {
  smtp_host?: string;
  smtp_port?: string;
  smtp_secure?: string | boolean;
  smtp_user?: string;
  smtp_pass?: string;
  smtp_from?: string;
  smtp_tls_reject_unauthorized?: string | boolean;
}

export interface TestSmtpResponse {
  success: boolean;
  message: string;
  data?: { status: string; message: string };
}

class ConfigurationService {
  async getInactivityLockTimeoutMinutes(): Promise<number> {
    try {
      const response: any = await api.get('/configurations/system-lock/inactivity-timeout');
      const minutes = Number(response?.data?.minutes ?? response?.minutes);
      return Number.isFinite(minutes) && minutes > 0 ? minutes : 15;
    } catch (error) {
      console.error('Erro ao buscar tempo de bloqueio por inatividade:', error);
      return 15;
    }
  }

  async updateInactivityLockTimeoutMinutes(minutes: number): Promise<number> {
    const response: any = await api.put('/configurations/system-lock/inactivity-timeout', { minutes });
    const updatedMinutes = Number(response?.data?.minutes ?? response?.minutes ?? minutes);
    return Number.isFinite(updatedMinutes) && updatedMinutes > 0 ? updatedMinutes : minutes;
  }

  /**
   * Lista todas as configurações
   */
  async getAllConfigurations(): Promise<Configuration[]> {
    try {
      const response = await api.get<ApiResponse<Configuration[]> | Configuration[]>('/configurations');

      // Verificar se a resposta é um array direto ou objeto estruturado
      if (Array.isArray(response.data)) {
        return response.data;
      }

      // Resposta é objeto estruturado
      const apiResponse = response.data as ApiResponse<Configuration[]>;
      if (apiResponse.success && apiResponse.data) {
        return apiResponse.data;
      }

      throw new Error(apiResponse.message || 'Erro ao buscar configurações');
    } catch (error: any) {
      console.error('❌ Erro no configurationService.getAllConfigurations:', error);

      if (error.code === 'NETWORK_ERROR' || error.message.includes('timeout')) {
        throw new Error('Erro de conexão com o servidor');
      }

      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Busca uma configuração específica por chave
   */
  async getConfigurationByKey(chave: string): Promise<Configuration> {
    try {
      const response = await api.get(`/configurations/${chave}`);

      // Se response.data já tem a estrutura {success, data}, acessa corretamente
      if (response.data && typeof response.data === 'object' && 'success' in response.data) {
        if (response.data.success && response.data.data) {
          return response.data.data;
        }
        throw new Error(response.data.message || 'Configuração não encontrada');
      }

      // Se response.data é diretamente a configuração (fallback)
      if (response.data && response.data.chave) {
        return response.data;
      }

      throw new Error('Estrutura de resposta inválida');
    } catch (error: any) {
      console.error('Erro ao buscar configuração:', error);

      // Se é erro 404, significa que a configuração não existe
      if (error.response?.status === 404) {
        throw new Error(`Configuração '${chave}' não encontrada`);
      }

      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Cria uma nova configuração
   */
  async createConfiguration(configData: CreateConfigurationRequest): Promise<Configuration> {
    try {
      if (!configData.chave || !configData.valor || !configData.descricao) {
        throw new Error('Chave, valor e descrição são obrigatórios');
      }

      const response = await api.post<ApiResponse<Configuration>>('/configurations', configData);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Erro ao criar configuração');
    } catch (error: any) {
      console.error('Erro ao criar configuração:', error);

      if (error.response?.status === 409) {
        throw new Error('Chave de configuração já existe');
      }

      if (error.response?.data?.errors) {
        throw new Error(error.response.data.errors.join(', '));
      }

      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Atualiza ou cria configuração (útil na primeira gravação de chaves novas, ex.: SMTP).
   */
  async upsertConfiguration(
    chave: string,
    valor: string,
    meta: { descricao: string; categoria: string; tipo?: CreateConfigurationRequest['tipo'] }
  ): Promise<Configuration> {
    try {
      const response: any = await api.put(`/configurations/${chave}`, { valor });
      if (response?.success && response?.data) return response.data as Configuration;
      if (response?.chave) return response as Configuration;
      throw new Error(response?.message || 'Erro ao atualizar configuração');
    } catch (error: any) {
      if (error.response?.status === 404) {
        return await this.createConfiguration({
          chave,
          valor,
          descricao: meta.descricao,
          tipo: meta.tipo || 'string',
          categoria: meta.categoria,
        });
      }
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Atualiza uma configuração existente
   */
  async updateConfiguration(chave: string, configData: UpdateConfigurationRequest): Promise<Configuration> {
    try {
      const response = await api.put<ApiResponse<Configuration>>(`/configurations/${chave}`, configData) as unknown as ApiResponse<Configuration>;

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Erro ao atualizar configuração');
    } catch (error: any) {
      console.error('Erro ao atualizar configuração:', error);

      if (error.response?.status === 404) {
        throw new Error('Configuração não encontrada');
      }

      if (error.response?.status === 409) {
        throw new Error('Chave de configuração já está em uso');
      }

      if (error.response?.data?.errors) {
        throw new Error(error.response.data.errors.join(', '));
      }

      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Erro interno do servidor'
      );
    }
  }

  // Configurações são fixas - não há método de exclusão

  /**
   * Busca configurações por categoria
   */
  async getConfigurationsByCategory(categoria: string): Promise<Configuration[]> {
    try {
      // Como não temos endpoint específico, vamos buscar todas e filtrar
      const allConfigurations = await this.getAllConfigurations();

      // Filtrar apenas configurações da categoria especificada
      const filteredConfigurations = allConfigurations.filter(config =>
        config.categoria === categoria && config.ativo
      );

      return filteredConfigurations;
    } catch (error: any) {
      console.error(`❌ Erro ao buscar configurações da categoria '${categoria}':`, error);

      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Testa o token da OpenAI
   */
  async testOpenAI(): Promise<TestOpenAIResponse> {
    try {
      // O interceptor do axios retorna response.data diretamente
      const responseData: any = await api.post('/configurations/test-openai');

      // O interceptor já retorna response.data, então responseData é o que precisamos
      if (responseData && typeof responseData === 'object') {
        // Se tem success definido (true ou false)
        if ('success' in responseData) {
          return {
            success: Boolean(responseData.success),
            message: responseData.message || 'Sem mensagem',
            model: responseData.model,
            usage: responseData.usage
          };
        }

        // Se não tem success, mas tem message (pode ser erro)
        if (responseData.message) {
          return {
            success: false,
            message: responseData.message
          };
        }
      }

      console.error('❌ Estrutura de resposta inesperada:', responseData);
      throw new Error('Resposta inválida do servidor: ' + JSON.stringify(responseData));
    } catch (error: any) {
      console.error('❌ Erro ao testar OpenAI:', error);

      // O interceptor já processou o erro, então error.response pode não existir
      // Mas se existir, vamos usar
      if (error.response?.data) {
        const errorData = error.response.data;

        // Se o erro tem success definido
        if (errorData && typeof errorData === 'object' && 'success' in errorData) {
          return {
            success: Boolean(errorData.success),
            message: errorData.message || 'Erro desconhecido'
          };
        }

        throw new Error(errorData?.message || 'Erro ao testar OpenAI');
      }

      // Se não tem response.data, usar a mensagem do erro diretamente
      throw new Error(error.message || 'Erro interno do servidor');
    }
  }

  /**
   * Testa conexão SMTP (valores do formulário; campos vazios usam o que está salvo no servidor).
   */
  async testSmtp(payload: TestSmtpPayload): Promise<TestSmtpResponse> {
    try {
      const responseData: any = await api.post('/configurations/test-smtp', payload);
      if (responseData && typeof responseData === 'object' && 'success' in responseData) {
        return {
          success: Boolean(responseData.success),
          message: responseData.message || '',
          data: responseData.data,
        };
      }
      throw new Error('Resposta inválida do servidor');
    } catch (error: any) {
      if (error.response?.data && typeof error.response.data === 'object') {
        const d = error.response.data;
        return {
          success: Boolean(d.success),
          message: d.message || 'Falha no teste SMTP',
          data: d.data,
        };
      }
      throw new Error(error.message || 'Erro ao testar SMTP');
    }
  }

  /**
   * Obtém a logo da empresa
   */
  async getLogo(): Promise<{ logo: string }> {
    try {
      const response: any = await api.get('/configurations/logo');

      if (response && typeof response === 'object') {
        if (response.success && response.data) {
          return response.data;
        }
        if (response.logo !== undefined) {
          return response;
        }
      }
      return { logo: '' };
    } catch (error) {
      console.error('Erro ao buscar logo:', error);
      return { logo: '' };
    }
  }

  /**
   * Faz upload da logo
   */
  async uploadLogo(file: File, onProgress?: (progress: number) => void): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response: any = await api.post('/configurations/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });

      return response;
    } catch (error: any) {
      console.error('Erro ao fazer upload da logo:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro interno do servidor',
      };
    }
  }

  /**
   * Gera URL completa da logo
   */
  getLogoUrl(): string {
    const baseUrl = import.meta.env.VITE_API_URL;
    return `${baseUrl}/uploads/logos/logo_provedor.png`;
  }

  getLogoUrlSysup(): string {
    const baseUrl = import.meta.env.VITE_API_URL;
    return `${baseUrl}/uploads/logos/logo_provedor_bkp.png`;
  }

  /**
   * Faz upload do ícone da logo
   */
  async uploadIconLogo(file: File, onProgress?: (progress: number) => void): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response: any = await api.post('/configurations/icon-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });

      return response;
    } catch (error: any) {
      console.error('Erro ao fazer upload do ícone da logo:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro interno do servidor',
      };
    }
  }

  /**
   * Gera URL do ícone da logo
   */
  getIconLogoUrl(): string {
    const baseUrl = import.meta.env.VITE_API_URL;
    return `${baseUrl}/uploads/logos/icon_logo.png`;
  }

  getIconLogoUrlSysup(): string {
    const baseUrl = import.meta.env.VITE_API_URL;
    return `${baseUrl}/uploads/logos/icon_logo_bkp.png`;
  }

  /**
   * Obtém a imagem de aviso na fatura
   */
  async getImagemFaturaAviso(): Promise<{ imagem: string }> {
    try {
      const response: any = await api.get('/configurations/imagem-fatura-aviso');

      if (response && typeof response === 'object') {
        if (response.success && response.data) {
          return response.data;
        }
        if (response.imagem !== undefined) {
          return response;
        }
      }
      return { imagem: '' };
    } catch (error) {
      console.error('Erro ao buscar imagem de aviso na fatura:', error);
      return { imagem: '' };
    }
  }

  /**
   * Faz upload da imagem de aviso na fatura
   */
  async uploadImagemFaturaAviso(file: File, onProgress?: (progress: number) => void): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const formData = new FormData();
      formData.append('imagem', file);

      const response: any = await api.post('/configurations/imagem-fatura-aviso', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });

      return response;
    } catch (error: any) {
      console.error('Erro ao fazer upload da imagem de aviso:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro interno do servidor',
      };
    }
  }

  /**
   * Remove a imagem de aviso na fatura
   */
  async removeImagemFaturaAviso(): Promise<{ success: boolean; message: string }> {
    try {
      const response: any = await api.delete('/configurations/imagem-fatura-aviso');
      return response;
    } catch (error: any) {
      console.error('Erro ao remover imagem de aviso:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro interno do servidor',
      };
    }
  }

  /**
   * Gera URL da imagem de aviso na fatura (pasta uploads na raiz)
   */
  getImagemFaturaAvisoUrl(filename?: string): string {
    if (!filename) return '';

    if (filename.startsWith('http://') || filename.startsWith('https://')) {
      return filename;
    }

    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    return `${baseUrl}/uploads/${filename}`;
  }

  async getImagemCapaCarneCustom(): Promise<{ imagem: string }> {
    try {
      const response: any = await api.get('/configurations/imagem-capa-carne-custom');
      if (response && typeof response === 'object') {
        if (response.success && response.data) return response.data;
        if (response.imagem !== undefined) return response;
      }
      return { imagem: '' };
    } catch (error) {
      console.error('Erro ao buscar imagem custom da capa do carnê:', error);
      return { imagem: '' };
    }
  }

  async uploadImagemCapaCarneCustom(file: File, onProgress?: (progress: number) => void): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const formData = new FormData();
      formData.append('imagem', file);

      const response: any = await api.post('/configurations/imagem-capa-carne-custom', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });

      return response;
    } catch (error: any) {
      console.error('Erro ao fazer upload da imagem custom da capa do carnê:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro interno do servidor',
      };
    }
  }

  async removeImagemCapaCarneCustom(): Promise<{ success: boolean; message: string }> {
    try {
      const response: any = await api.delete('/configurations/imagem-capa-carne-custom');
      return response;
    } catch (error: any) {
      console.error('Erro ao remover imagem custom da capa do carnê:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro interno do servidor',
      };
    }
  }

  getImagemCapaCarneCustomUrl(filename?: string): string {
    if (!filename) return '';
    if (filename.startsWith('http://') || filename.startsWith('https://')) {
      return filename;
    }
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    return `${baseUrl}/uploads/${filename}`;
  }

  async getImagemContraCapaCarneCustom(): Promise<{ imagem: string }> {
    try {
      const response: any = await api.get('/configurations/imagem-contra-capa-carne-custom');
      if (response && typeof response === 'object') {
        if (response.success && response.data) return response.data;
        if (response.imagem !== undefined) return response;
      }
      return { imagem: '' };
    } catch (error) {
      console.error('Erro ao buscar imagem custom da contracapa do carnê:', error);
      return { imagem: '' };
    }
  }

  async uploadImagemContraCapaCarneCustom(file: File, onProgress?: (progress: number) => void): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const formData = new FormData();
      formData.append('imagem', file);

      const response: any = await api.post('/configurations/imagem-contra-capa-carne-custom', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });

      return response;
    } catch (error: any) {
      console.error('Erro ao fazer upload da imagem custom da contracapa do carnê:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro interno do servidor',
      };
    }
  }

  async removeImagemContraCapaCarneCustom(): Promise<{ success: boolean; message: string }> {
    try {
      const response: any = await api.delete('/configurations/imagem-contra-capa-carne-custom');
      return response;
    } catch (error: any) {
      console.error('Erro ao remover imagem custom da contracapa do carnê:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro interno do servidor',
      };
    }
  }

  getImagemContraCapaCarneCustomUrl(filename?: string): string {
    if (!filename) return '';
    if (filename.startsWith('http://') || filename.startsWith('https://')) {
      return filename;
    }
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    return `${baseUrl}/uploads/${filename}`;
  }

  /**
   * Obtém a imagem do dashboard do app do cliente
   */
  async getAppClienteDashboardImg(): Promise<{ imagem: string }> {
    try {
      const response: any = await api.get('/configurations/app-cliente-dashboard-img');

      if (response && typeof response === 'object') {
        if (response.success && response.data) {
          return response.data;
        }
        if (response.imagem !== undefined) {
          return response;
        }
      }
      return { imagem: '' };
    } catch (error) {
      console.error('Erro ao buscar imagem do dashboard do app do cliente:', error);
      return { imagem: '' };
    }
  }

  /**
   * Faz upload da imagem do dashboard do app do cliente
   */
  async uploadAppClienteDashboardImg(file: File, onProgress?: (progress: number) => void): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const formData = new FormData();
      formData.append('imagem', file);

      const response: any = await api.post('/configurations/app-cliente-dashboard-img', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });

      return response;
    } catch (error: any) {
      console.error('Erro ao fazer upload da imagem do dashboard:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro interno do servidor',
      };
    }
  }

  /**
   * Remove a imagem do dashboard do app do cliente
   */
  async removeAppClienteDashboardImg(): Promise<{ success: boolean; message: string }> {
    try {
      const response: any = await api.delete('/configurations/app-cliente-dashboard-img');
      return response;
    } catch (error: any) {
      console.error('Erro ao remover imagem do dashboard:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro interno do servidor',
      };
    }
  }

  /**
   * Gera URL da imagem do dashboard do app do cliente
   */
  getAppClienteDashboardImgUrl(filename?: string): string {
    if (!filename) return '';

    if (filename.startsWith('http://') || filename.startsWith('https://')) {
      return filename;
    }

    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    return `${baseUrl}/uploads/${filename}`;
  }

  /**
   * Executa script de atualização de marca do AppCliente (manifest.json e index.html)
   */
  async updateAppClienteBranding(provedorNome?: string): Promise<{ success: boolean; message: string }> {
    try {
      const response: any = await api.post('/configurations/update-app-branding', { provedor_nome: provedorNome });
      if (response && typeof response === 'object') {
        return {
          success: Boolean(response.success),
          message: response.message || 'AppCliente atualizado com sucesso!'
        };
      }
      return { success: true, message: 'AppCliente atualizado com sucesso!' };
    } catch (error: any) {
      console.error('Erro ao atualizar AppCliente:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erro ao atualizar AppCliente'
      };
    }
  }

  /**
   * Executa script de atualização de marca do AppTecnico (manifest.json e index.html)
   */
  async updateAppTecnicoBranding(provedorNome?: string): Promise<{ success: boolean; message: string }> {
    try {
      const response: any = await api.post('/configurations/update-app-tecnico-branding', { provedor_nome: provedorNome });
      if (response && typeof response === 'object') {
        return {
          success: Boolean(response.success),
          message: response.message || 'AppTécnico atualizado com sucesso!'
        };
      }
      return { success: true, message: 'AppTécnico atualizado com sucesso!' };
    } catch (error: any) {
      console.error('Erro ao atualizar AppTécnico:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erro ao atualizar AppTécnico'
      };
    }
  }
}

export const configurationService = new ConfigurationService();
export default configurationService; 
